"use client";

import { getMoneyFormatLocale } from "@/i18n/types";
import { useLocale } from "next-intl";
import { useMemo } from "react";

import {
  findCategoryLabel,
  formatPriceLabel,
} from "../helpers/filtersChips.helper";
import type { ChipItem } from "../helpers/filtersChips.helper";
import { useFiltersStore } from "../stores/filters.store";
import type {
  CustomCategoryFacet,
  CustomColorFacet,
  CustomPriceFacet,
  DefaultListFacet,
  Facet,
  PendingFilters,
} from "../types/filters.type";

function isListFacet(
  facet: Facet,
): facet is DefaultListFacet | CustomColorFacet {
  return (
    (facet.component === "Default" && facet.selector !== "Boolean") ||
    facet.component === "CustomColor"
  );
}

function resolveListChips(
  facet: DefaultListFacet | CustomColorFacet,
): ChipItem[] {
  return facet.values
    .filter((v) => v.is_selected)
    .map((v) => ({ field: facet.field, value: v.value, label: v.label }));
}

function resolvePriceChip(
  facet: CustomPriceFacet,
  pendingFilters: PendingFilters | null,
  locale: string,
): ChipItem | null {
  const min = pendingFilters?.price_min;
  const max = pendingFilters?.price_max;
  if (min == null || max == null) return null;
  return {
    field: facet.field,
    value: `${min},${max}`,
    label: formatPriceLabel(min, max, facet.currency, locale),
  };
}

function resolveCategoryIdChips(
  facet: CustomCategoryFacet,
  pendingFilters: PendingFilters | null,
): ChipItem[] {
  const catIds = pendingFilters?.categories_ids;
  if (!catIds || catIds.length === 0) return [];
  return catIds.map((id) => ({
    field: "categories_ids",
    value: id,
    label: findCategoryLabel(facet.tree, id) ?? id,
  }));
}

function resolveCategoryPathChip(
  pendingFilters: PendingFilters | null,
): ChipItem | null {
  const path = pendingFilters?.pending_category_path;
  if (!path) return null;
  const pathSegments = path.split("/").filter(Boolean);
  const lastSegment = pathSegments.pop();
  const label = lastSegment
    ? lastSegment.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : path;
  return { field: "pending_category_path", value: path, label };
}

function resolveChipsForFacet(
  facet: Facet,
  pendingFilters: PendingFilters | null,
  locale: string,
): ChipItem[] {
  if (facet.component === "Default" && facet.selector === "Boolean") return [];
  if (isListFacet(facet)) return resolveListChips(facet);
  if (facet.component === "CustomPrice") {
    const chip = resolvePriceChip(facet, pendingFilters, locale);
    return chip ? [chip] : [];
  }
  if (facet.component === "CustomCategory") {
    const pathChip = resolveCategoryPathChip(pendingFilters);
    return [
      ...resolveCategoryIdChips(facet, pendingFilters),
      ...(pathChip ? [pathChip] : []),
    ];
  }
  return [];
}

export function useFilteredItems(): ChipItem[] {
  const pendingFilters = useFiltersStore((s) => s.pendingFilters);
  const draftFacets = useFiltersStore((s) => s.draftFacets);
  const moneyLocale = getMoneyFormatLocale(useLocale());

  return useMemo(
    () =>
      draftFacets?.flatMap((facet) =>
        resolveChipsForFacet(facet, pendingFilters, moneyLocale),
      ) ?? [],
    [draftFacets, pendingFilters, moneyLocale],
  );
}
